<template>
  <box :title="t('Desempenho por Dispositivo')" type="primary">
    <data-table
      v-if="devices && devices.length > 0"
      :data="formattedDevices"
      :config="tableConfig"
    />
    <div v-else class="flex justify-content-center p-3">
      <span class="text-color-secondary">{{ t('Nenhum dispositivo encontrado') }}</span>
    </div>
  </box>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface DeviceRow {
  device_name?: string;
  provider_type?: string;
  franquias?: string;
  connection_status?: string;
  mensagens_enviadas?: number;
  mensagens_recebidas?: number;
  chats_nao_lidos?: number;
  score_performance?: number;
  [key: string]: unknown;
}

interface FormattedDeviceRow extends DeviceRow {
  score_formatado: string;
}

interface CellParams {
  data?: Record<string, unknown>;
  value?: unknown;
}

const props = withDefaults(defineProps<{
  devices?: DeviceRow[];
}>(), {
  devices: () => []
});

const formattedDevices = computed<FormattedDeviceRow[]>(() => {
  return props.devices.map((d) => ({
    ...d,
    score_formatado: d.score_performance !== undefined ? d.score_performance.toFixed(1) : '0'
  }));
});

const tableColumns = computed(() => [
  {
    data: 'device_name',
    title: t('Dispositivo'),
    width: 150,
    resizable: true,
    sortable: true
  },
  {
    data: 'provider_type',
    title: t('Tipo'),
    width: 100,
    resizable: true,
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      const type = (params.value as string) || 'whatsapp';
      const icon = type === 'instagram' ? 'pi pi-instagram' : 'pi pi-whatsapp';
      const color = type === 'instagram' ? '#E4405F' : '#25D366';
      return `<span style="color: ${color};"><i class="${icon}"></i> ${type.charAt(0).toUpperCase() + type.slice(1)}</span>`;
    }
  },
  {
    data: 'franquias',
    title: t('Franquias'),
    width: 180,
    resizable: true,
    sortable: true
  },
  {
    data: 'connection_status',
    title: t('Status'),
    width: 100,
    resizable: true,
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      const status = (params.value as string) || '';
      const color = status === 'open' ? '#22c55e' : '#ef4444';
      const label = status === 'open' ? 'Online' : (status || 'Offline');
      return `<span style="color: ${color}; font-weight: 600;">● ${label}</span>`;
    }
  },
  {
    data: 'mensagens_enviadas',
    title: t('Enviadas'),
    width: 100,
    resizable: true,
    sortable: true,
    dataType: 'number',
    footerType: 'sum'
  },
  {
    data: 'mensagens_recebidas',
    title: t('Recebidas'),
    width: 100,
    resizable: true,
    sortable: true,
    dataType: 'number',
    footerType: 'sum'
  },
  {
    data: 'chats_nao_lidos',
    title: t('Não Lidos'),
    width: 110,
    resizable: true,
    sortable: true,
    dataType: 'number',
    cellRenderer: (params: CellParams): string => {
      const val = (params.value as number) || 0;
      const color = val === 0 ? '#22c55e' : (val <= 5 ? '#f59e0b' : '#ef4444');
      return `<span style="color: ${color}; font-weight: 600;">${val}</span>`;
    }
  },
  {
    data: 'score_formatado',
    title: t('Score'),
    width: 90,
    resizable: true,
    sortable: true,
    cellRenderer: (params: CellParams): string => {
      const score = parseFloat(String(params.value ?? '0')) || 0;
      const color = score >= 80 ? '#22c55e' : (score >= 60 ? '#f59e0b' : '#ef4444');
      return `<span style="color: ${color}; font-weight: 700; font-size: 1.1em;">${params.value}</span>`;
    }
  }
]);

const tableConfig = computed(() => ({
  tableId: 'whatsappAnalyticsDevices',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.device_id ?? data?.whatsappfranquia_id ?? data?.device_name;
    return id == null ? '' : `messaging-analytics-device-row-${id}`;
  },
  title: '',
  filename: `whatsapp_analytics_devices_${new Date().toISOString().split('T')[0]}`,
  columns: tableColumns.value,
  pageLength: 50,
  height: '400px',
  pagination: false,
  footer: true,
  footerLabel: t('Totais'),
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small'
}));
</script>
